import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { ArrowLeft, ArrowRight, Loader2, Mail, Truck, ShieldCheck, BadgeCheck, X, Gift, ShoppingBag } from "lucide-react";
import api, { formatMoney, formatApiErrorDetail, originalPrice, bogoTier } from "../lib/apiClient";
import { track } from "../lib/posthog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { PaymentForm } from "../components/PaymentForm";

const newCheckoutId = () =>
  (crypto?.randomUUID ? crypto.randomUUID() : "co-" + Math.random().toString(36).slice(2) + Date.now());

const emailValid = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
const FALLBACK_COUNTRIES = [
  { code: "US", name: "United States" }, { code: "CA", name: "Canada" },
  { code: "GB", name: "United Kingdom" },
];

export default function Checkout() {
  const { items, subtotal, clearCart, trackAbandoned, freeGift, bogo, saleOn } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [checkoutId] = useState(() => {
    let id = sessionStorage.getItem("ethos_checkout_id");
    if (!id) { id = newCheckoutId(); sessionStorage.setItem("ethos_checkout_id", id); }
    return id;
  });

  const [cfg, setCfg] = useState(null);
  const [cfgError, setCfgError] = useState("");
  const [countries, setCountries] = useState(FALLBACK_COUNTRIES);
  const [email, setEmail] = useState("");
  const [addr, setAddr] = useState({ name: "", address1: "", address2: "", city: "", state: "", postal_code: "", country: "US" });
  const [rates, setRates] = useState(null);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [methodId, setMethodId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const [step, setStep] = useState("details"); // details | pay
  const stripePromiseRef = useRef(null);
  const intentPromiseRef = useRef(null);

  // Discounts — multiple coupon codes may stack unless one is "individual use"
  const [couponInput, setCouponInput] = useState("");
  const [coupons, setCoupons] = useState([]); // [{code, amount, individual_use}]
  const [couponMsg, setCouponMsg] = useState("");
  const [couponBusy, setCouponBusy] = useState(false);

  // Shipping Protection (default off) — $6.99 + 5% of order value
  const [protection, setProtection] = useState(false);
  const [note, setNote] = useState("");

  useEffect(() => { if (user && user.email && !email) setEmail(user.email); /* eslint-disable-next-line */ }, [user]);

  useEffect(() => {
    api.get("/checkout/config").then((r) => setCfg(r.data)).catch(() => setCfgError("Checkout is temporarily unavailable. Please try again shortly."));
    api.get("/checkout/countries").then((r) => setCountries(r.data.countries || FALLBACK_COUNTRIES)).catch(() => {});
  }, []);

  const startedRef = useRef(false);
  useEffect(() => {
    if (!startedRef.current && items.length) {
      startedRef.current = true;
      track("checkout_started", {
        value: Number(subtotal.toFixed(2)),
        num_items: items.reduce((n, i) => n + i.quantity, 0),
      });
    }
  }, [items, subtotal]);

  const newsletterEligible = !!(user && user.newsletter && !user.newsletter_discount_used);
  const newsletterDiscount = newsletterEligible ? Math.round(subtotal * 0.20 * 100) / 100 : 0;

  const applyCoupon = async () => {
    const code = couponInput.trim();
    if (!code || couponBusy) return;
    if (coupons.some((c) => c.code.toLowerCase() === code.toLowerCase())) {
      setCouponMsg("That code is already applied."); return;
    }
    setCouponBusy(true); setCouponMsg("");
    try {
      const { data } = await api.get("/checkout/validate-coupon", { params: { code, subtotal } });
      if (!data.valid) { setCouponMsg(data.detail || "That code isn't valid."); return; }
      if (coupons.length && (data.individual_use || coupons.some((c) => c.individual_use))) {
        setCouponMsg(`"${data.code}" can't be combined with another code.`); return;
      }
      setCoupons((cs) => [...cs, { code: data.code, amount: data.amount, individual_use: !!data.individual_use }]);
      setCouponInput(""); setCouponMsg("");
    } catch { setCouponMsg("Could not validate that code."); }
    finally { setCouponBusy(false); }
  };

  const removeCoupon = (code) => { setCoupons((cs) => cs.filter((c) => c.code !== code)); setCouponMsg(""); };

  const lines = useMemo(() => items.map((i) => ({ product_id: String(i.product_id), quantity: i.quantity, variation_id: i.variation_id || null })), [items]);

  const latestReq = useRef(0);
  const fetchRates = useCallback(async (country) => {
    if (!items.length) return;
    const reqId = ++latestReq.current;
    setRatesLoading(true);
    try {
      const { data } = await api.post("/checkout/shipping-rates", { country, items: lines });
      if (reqId !== latestReq.current) return; // ignore out-of-order responses
      setRates(data);
      setMethodId(data.supported && data.methods.length ? data.methods[0].id : "");
    } catch {
      if (reqId === latestReq.current) { setRates({ supported: false, methods: [] }); setMethodId(""); }
    } finally {
      if (reqId === latestReq.current) setRatesLoading(false);
    }
  }, [items.length, lines]);

  useEffect(() => { fetchRates(addr.country); /* eslint-disable-next-line */ }, [addr.country, fetchRates]);

  const selectedMethod = rates?.methods?.find((m) => m.id === methodId) || null;
  const shippingCost = selectedMethod ? selectedMethod.cost : 0;
  const couponDiscount = coupons.reduce((s, c) => s + (c.amount || 0), 0);
  // Newsletter first-order discount and coupons never stack — the larger single
  // discount applies (matches backend; prevents a 20% coupon + 20% newsletter = 40%).
  const useCoupon = couponDiscount > 0 && couponDiscount >= newsletterDiscount;
  const appliedNewsletter = useCoupon ? 0 : newsletterDiscount;
  const appliedCoupon = useCoupon ? couponDiscount : (newsletterDiscount > 0 ? 0 : couponDiscount);
  // Buy X Get X Free — when active it GOVERNS pricing: per-line % off paid items,
  // and coupon/newsletter discounts do NOT stack on top (matches backend).
  const bogoDiscount = bogo
    ? Math.round(items.reduce((s, i) => s + i.price * i.quantity * bogoTier(i.quantity).pct, 0) * 100) / 100
    : 0;
  const discountTotal = bogo
    ? Math.min(subtotal, Math.round((bogoDiscount + couponDiscount) * 100) / 100)
    : Math.min(subtotal, Math.round((appliedNewsletter + appliedCoupon) * 100) / 100);
  const protectionCost = protection ? Math.round((6.99 + subtotal * 0.05) * 100) / 100 : 0;
  const total = Math.max(0, subtotal - discountTotal) + shippingCost + protectionCost;
  const giftEligible = !bogo && !!(freeGift && (subtotal - discountTotal) >= freeGift.threshold);

  const addressComplete = addr.name.trim() && addr.address1.trim() && addr.city.trim() && addr.postal_code.trim() && addr.country;
  const canContinue = emailValid(email) && addressComplete && rates?.supported && methodId;

  const setField = (k) => (e) => setAddr((a) => ({ ...a, [k]: e.target.value }));

  const ensureConfig = async () => {
    if (cfg?.payments_enabled && cfg?.publishable_key) return cfg;
    // Retry once — the hub config call can time out on a cold cache.
    for (let i = 0; i < 2; i++) {
      try {
        const { data } = await api.get("/checkout/config", { timeout: 25000 });
        setCfg(data);
        if (data?.payments_enabled && data?.publishable_key) return data;
      } catch (e) { console.debug("checkout config retry failed", e); }
    }
    return cfg;
  };

  const goToPayment = async () => {
    if (!canContinue || busy) return;
    setBusy(true); setError("");
    try {
      const c = await ensureConfig();
      if (!c?.payments_enabled || !c?.publishable_key) {
        setError("Payments are currently unavailable. Please try again shortly.");
        setBusy(false);
        return;
      }
      stripePromiseRef.current = loadStripe(c.publishable_key, { stripeAccount: c.stripe_account });
      setStep("pay");
      window.scrollTo({ top: 0, behavior: "smooth" });
      // Prefetch the PaymentIntent now so the hub latency overlaps card entry.
      intentPromiseRef.current = requestIntent();
      intentPromiseRef.current.catch(() => {}); // silence if shopper never pays
      trackAbandoned({ source: "attestly_checkout", payment_intent_status: "Payment Started", email });
    } catch {
      setError("Could not open the payment step. Please try again.");
    } finally { setBusy(false); }
  };

  // Single in-flight create-intent request (deferred flow).
  const requestIntent = async () => {
    const { data } = await api.post("/checkout/create-intent",
      { checkout_id: checkoutId, email, items: lines, shipping: addr, shipping_method_id: methodId, coupon_codes: coupons.map((c) => c.code), shipping_protection: protection, note: note.trim() || null },
      { timeout: 60000 });
    return { clientSecret: data.client_secret, orderNumber: data.order_number };
  };

  // Used by PaymentForm at Pay time — reuses the prefetched intent when ready.
  const createIntent = async () => {
    if (!intentPromiseRef.current) intentPromiseRef.current = requestIntent();
    try {
      return await intentPromiseRef.current;
    } catch (err) {
      intentPromiseRef.current = null; // allow a fresh retry on next click
      throw err;
    }
  };

  const onPaid = (order) => {
    track("purchase", {
      order_number: order.order_number,
      value: Number((order.total || 0).toFixed(2)),
      currency: order.currency || "usd",
    });
    sessionStorage.removeItem("ethos_checkout_id");
    if (order.access_token) sessionStorage.setItem(`eth_ot_${order.order_number}`, order.access_token);
    clearCart();
    navigate(`/order/${order.order_number}`);
  };

  const appearance = useMemo(() => ({ theme: "flat", variables: { colorPrimary: "#8a681c", borderRadius: "10px", fontFamily: "Hanken Grotesque, sans-serif" } }), []);

  if (!items.length && step !== "pay") {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="checkout-empty">
        <p className="text-muted-foreground">Your cart is empty.</p>
        <Link to="/shop" data-testid="checkout-empty-shop" className="font-semibold mt-4 inline-block underline underline-offset-4">Continue shopping</Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="native-checkout-page">
      <Link to="/shop" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8" data-testid="checkout-back"><ArrowLeft size={16} aria-hidden="true" /> Back to shop</Link>
      <span className="eyebrow block">Secure checkout</span>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3 mb-10 leading-[1.05]" data-testid="checkout-heading">Checkout</h1>

      {cfgError && <p role="alert" className="text-sm text-destructive p-4 rounded-xl border border-destructive/30 mb-6" data-testid="checkout-config-error">{cfgError}</p>}
      {cfg && !cfg.payments_enabled && <p role="alert" className="text-sm text-amber-800 bg-amber-100/70 border border-amber-200 rounded-xl p-4 mb-6" data-testid="checkout-payments-disabled">Payments are currently unavailable. Please check back soon.</p>}

      <div className="grid lg:grid-cols-3 gap-8 items-start">
        <div className="lg:col-span-2 space-y-5">
          {step === "details" && (
            <section className="card-soft rounded-2xl p-6 sm:p-8 space-y-6" data-testid="checkout-contact-step">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="rounded-2xl p-3 bg-gradient-to-br from-[#f4e7c4] to-[#e6c665]/50 ring-1 ring-[#e6c665]/40"><Mail className="h-6 w-6 text-[#8a681c]" aria-hidden="true" /></div>
                  <div><h2 className="font-display font-bold text-lg">Contact</h2><p className="text-xs text-muted-foreground">For your receipt and order updates</p></div>
                </div>
                <div>
                  <label htmlFor="checkout-email" className="text-sm font-medium">Email address</label>
                  <Input id="checkout-email" type="email" value={email} data-testid="checkout-email-input" onChange={(e) => setEmail(e.target.value)} placeholder="you@lab.edu" className="mt-2" />
                </div>
              </div>

              <div className="space-y-4 border-t border-border pt-6">
                <div className="flex items-center gap-3">
                  <div className="rounded-2xl p-3 bg-gradient-to-br from-[#f4e7c4] to-[#e6c665]/50 ring-1 ring-[#e6c665]/40"><Truck className="h-6 w-6 text-[#8a681c]" aria-hidden="true" /></div>
                  <div><h2 className="font-display font-bold text-lg">Shipping address</h2><p className="text-xs text-muted-foreground">Where should we send your order?</p></div>
                </div>
                <div>
                  <label htmlFor="ship-country" className="text-sm font-medium">Country</label>
                  <select id="ship-country" data-testid="ship-country" value={addr.country} onChange={setField("country")} className="mt-2 w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
                    {countries.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                  </select>
                </div>
                <Input value={addr.name} data-testid="ship-name" onChange={setField("name")} placeholder="Full name" />
                <Input value={addr.address1} data-testid="ship-address1" onChange={setField("address1")} placeholder="Address line 1" />
                <Input value={addr.address2} data-testid="ship-address2" onChange={setField("address2")} placeholder="Address line 2 (optional)" />
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Input value={addr.city} data-testid="ship-city" onChange={setField("city")} placeholder="City" />
                  <Input value={addr.state} data-testid="ship-state" onChange={setField("state")} placeholder="State / Province" />
                  <Input value={addr.postal_code} data-testid="ship-postal" onChange={setField("postal_code")} placeholder="ZIP / Postal" />
                </div>
              </div>

              <div className="border-t border-border pt-6">
                <button
                  type="button"
                  onClick={() => setProtection((p) => !p)}
                  data-testid="shipping-protection-toggle"
                  aria-pressed={protection}
                  className="w-full text-left rounded-2xl p-5 border transition-colors"
                  style={{ background: "linear-gradient(135deg, #fdf3d8 0%, #fbe7bd 100%)", borderColor: protection ? "#c59b27" : "#eadfc0" }}
                >
                  <div className="flex items-start gap-3">
                    <ShieldCheck className="h-6 w-6 shrink-0 text-[#8a681c] mt-0.5" aria-hidden="true" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <h3 className="font-display font-bold text-lg text-[#3a2f12]">Shipping Protection</h3>
                        <span className="font-display font-bold text-lg text-[#3a2f12] shrink-0" data-testid="shipping-protection-cost">{formatMoney(protectionCost)}</span>
                      </div>
                      <p className="text-sm text-[#6b5a2e] mt-1">Loss, theft &amp; damage in transit.</p>
                      <p className="text-sm text-[#6b5a2e]">$6.99 + 5% of order value</p>
                      <div className="flex items-center gap-2.5 mt-3">
                        <span className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${protection ? "bg-[#c59b27]" : "bg-[#d8cba6]"}`}>
                          <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${protection ? "translate-x-5" : "translate-x-0.5"}`} />
                        </span>
                        <span className="text-sm font-semibold text-[#3a2f12]">{protection ? "Protecting this order" : "Not protected"}</span>
                      </div>
                    </div>
                  </div>
                </button>
              </div>

              <div className="space-y-3 border-t border-border pt-6">
                <h3 className="font-display font-bold text-base">Shipping method</h3>
                {ratesLoading && <p className="text-sm text-muted-foreground flex items-center gap-2" data-testid="ship-rates-loading"><Loader2 className="animate-spin h-4 w-4" aria-hidden="true" /> Calculating rates…</p>}
                {!ratesLoading && rates && !rates.supported && <p className="text-sm text-amber-800 bg-amber-100/70 border border-amber-200 rounded-xl p-3" data-testid="ship-unsupported">We don’t currently ship to this country.</p>}
                {!ratesLoading && rates?.supported && (
                  <div className="space-y-2" data-testid="ship-methods">
                    {rates.methods.map((m) => (
                      <label key={m.id} data-testid={`ship-method-${m.id}`} className={`flex items-center justify-between gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${methodId === m.id ? "border-[#8a681c] bg-[#faf7ee]" : "border-border bg-white"}`}>
                        <span className="flex items-center gap-3 text-sm">
                          <input type="radio" name="ship-method" value={m.id} checked={methodId === m.id} onChange={() => setMethodId(m.id)} className="accent-[#8a681c]" />
                          {m.label}
                        </span>
                        <span className="font-semibold text-sm">{m.cost === 0 ? "Free" : formatMoney(m.cost)}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2 border-t border-border pt-6">
                <h3 className="font-display font-bold text-base">Order notes <span className="text-xs font-normal text-muted-foreground">(optional)</span></h3>
                <Textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} data-testid="checkout-order-note" placeholder="Anything we should know about your order? (visible to our fulfillment team)" className="min-h-24 bg-white" />
              </div>

              {error && <p role="alert" className="text-sm text-destructive p-3 rounded-xl border border-destructive/30" data-testid="checkout-contact-error">{error}</p>}

              <p className="text-xs leading-relaxed text-muted-foreground" data-testid="checkout-billing-descriptor">
                Your card statement will show <span className="font-semibold text-foreground">ETHOS RESEARCH LABS LLC</span>.
              </p>

              <Button type="button" onClick={goToPayment} disabled={!canContinue || busy} data-testid="checkout-continue-button" className="btn-gold w-full min-h-12 h-auto py-3.5 rounded-xl font-semibold text-base">
                {busy ? <><Loader2 className="animate-spin" aria-hidden="true" /> Preparing payment…</> : <>Continue to payment <ArrowRight aria-hidden="true" /></>}
              </Button>
            </section>
          )}

          {step === "pay" && stripePromiseRef.current && (
            <section className="card-soft rounded-2xl p-6 sm:p-8 space-y-6" data-testid="checkout-payment-step">
              <div className="flex items-center justify-between">
                <div><h2 className="font-display font-bold text-lg">Payment</h2><p className="text-xs text-muted-foreground">{formatMoney(total)} · {selectedMethod?.label}</p></div>
                <button type="button" onClick={() => { setStep("details"); intentPromiseRef.current = null; }} className="text-xs text-muted-foreground underline underline-offset-4" data-testid="checkout-edit-contact">Edit details</button>
              </div>
              <Elements stripe={stripePromiseRef.current} options={{ mode: "payment", amount: Math.round(total * 100), currency: "usd", appearance }}>
                <PaymentForm total={total} createIntent={createIntent} onPaid={onPaid} />
              </Elements>
            </section>
          )}
        </div>

        <aside className="card-soft rounded-2xl p-6 lg:sticky lg:top-24" aria-label="Cart summary">
          <h2 className="font-display font-bold text-lg mb-5">Your cart</h2>
          <div className="space-y-3 max-h-72 overflow-y-auto">
            <div className="rounded-2xl card-soft p-3 space-y-3" data-testid="checkout-paid-card">
              {bogo && <p className="eyebrow text-muted-foreground flex items-center gap-1"><ShoppingBag className="w-3.5 h-3.5" /> Your vials</p>}
              {items.map((item) => (
                <div key={item.product_id} className="flex gap-3 items-center" data-testid={`checkout-item-${item.product_id}`}>
                  <img src={item.image_url} alt={item.name} className="w-12 h-12 rounded-lg object-cover bg-secondary" />
                  <div className="flex-1 min-w-0"><p className="text-sm font-medium break-words">{item.name}</p><p className="text-xs text-muted-foreground">Qty {item.quantity}</p></div>
                  <p className="text-sm text-right whitespace-nowrap">{saleOn && <span className="text-muted-foreground line-through mr-1.5 text-xs">{formatMoney(originalPrice(item.price) * item.quantity)}</span>}<span className="font-semibold">{formatMoney(item.price * item.quantity)}</span></p>
                </div>
              ))}
            </div>
            {bogo && items.some((i) => bogoTier(i.quantity).free > 0) && (
              <div className="rounded-2xl border border-[#e6c665]/60 bg-[#fdf6e3] p-3 space-y-2" data-testid="checkout-free-card">
                <p className="eyebrow text-[#8a681c] flex items-center gap-1"><Gift className="w-3.5 h-3.5" /> Free with BOGO</p>
                {items.map((item) => {
                  const t = bogoTier(item.quantity);
                  if (t.free <= 0) return null;
                  return (
                    <div key={`free-${item.product_id}`} className="flex gap-3 items-center" data-testid={`checkout-bogo-free-${item.product_id}`}>
                      <img src={item.image_url} alt={item.name} className="w-12 h-12 rounded-lg object-cover bg-secondary ring-1 ring-[#c59b27]/40" />
                      <div className="flex-1 min-w-0"><p className="text-sm font-medium break-words">{item.name}</p><p className="text-xs text-[#8a681c] font-semibold">Complimentary · Qty {t.free}</p></div>
                      <p className="text-sm text-right font-semibold text-[#8a681c]">FREE</p>
                    </div>
                  );
                })}
              </div>
            )}
            {giftEligible && (
              <div className="flex gap-3 items-center" data-testid="checkout-free-gift">
                <img src={freeGift.gift.image_url} alt={freeGift.gift.name} className="w-12 h-12 rounded-lg object-cover bg-secondary ring-1 ring-[#c59b27]/40" />
                <div className="flex-1 min-w-0"><p className="text-sm font-medium break-words">{freeGift.gift.name}</p><p className="text-xs text-[#8a681c] font-semibold">Complimentary gift · Qty 1</p></div>
                <p className="text-sm text-right font-semibold text-[#8a681c]" data-testid="checkout-free-gift-price">FREE</p>
              </div>
            )}
          </div>
          <div className="border-t border-border mt-5 pt-5 space-y-3">
            <div data-testid="checkout-promo">
              {bogo && <p className="rounded-xl border border-[#e6c665]/60 bg-[#fdf6e3] px-3 py-2 text-[11px] text-[#6b5a2e] mb-1" data-testid="checkout-bogo-note">Have a referral code? Codes of 10% or less stack with the Buy X Get X Free promotion.</p>}
              <label className="text-xs font-bold text-muted-foreground uppercase tracking-wide">Promo code</label>
              <div className="flex gap-2 mt-2">
                <Input value={couponInput} onChange={(e) => setCouponInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyCoupon(); } }} placeholder="Enter code" className="h-10 bg-white" data-testid="checkout-promo-input" />
                <Button type="button" onClick={applyCoupon} disabled={couponBusy || !couponInput.trim()} className="h-10 px-4 rounded-xl" data-testid="checkout-promo-apply">
                  {couponBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
                </Button>
              </div>
              {couponMsg && <p className="text-xs text-destructive mt-1" data-testid="checkout-promo-error">{couponMsg}</p>}
              {coupons.length > 0 && (
                <div className="mt-3 space-y-2" data-testid="checkout-promo-applied">
                  {coupons.map((c) => (
                    <div key={c.code} className="flex items-center justify-between gap-2 rounded-xl border-2 border-emerald-500/40 bg-emerald-50 px-3 py-2" data-testid={`checkout-promo-chip-${c.code}`}>
                      <span className="flex items-center gap-2 min-w-0">
                        <BadgeCheck className="w-4 h-4 text-emerald-600 shrink-0" />
                        <span className="font-display font-bold text-sm text-emerald-800 uppercase tracking-wide truncate">{c.code}</span>
                        <span className="text-xs font-semibold text-emerald-700 shrink-0">−{formatMoney(c.amount)}</span>
                      </span>
                      <button type="button" onClick={() => removeCoupon(c.code)} className="text-emerald-700/70 hover:text-emerald-900 shrink-0" aria-label={`Remove ${c.code}`} data-testid={`checkout-promo-remove-${c.code}`}>
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <p className="text-[11px] text-muted-foreground">Add more codes to stack savings, unless a code is single-use only.</p>
                </div>
              )}
            </div>
          </div>
          <div className="border-t border-border mt-5 pt-5 space-y-2">
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Subtotal</span><span data-testid="checkout-subtotal">{formatMoney(subtotal)}</span></div>
            {bogo && bogoDiscount > 0 && <div className="flex justify-between text-sm text-[#8a681c] font-semibold"><span>Buy X Get X Free — % off</span><span data-testid="checkout-bogo-discount">−{formatMoney(bogoDiscount)}</span></div>}
            {bogo && coupons.map((c) => (
              <div key={c.code} className="flex justify-between text-sm text-emerald-700"><span>Coupon {c.code}</span><span data-testid={`checkout-coupon-discount-${c.code}`}>−{formatMoney(c.amount)}</span></div>
            ))}
            {!bogo && appliedNewsletter > 0 && <div className="flex justify-between text-sm text-emerald-700"><span>Newsletter 20% off</span><span data-testid="checkout-newsletter-discount">−{formatMoney(appliedNewsletter)}</span></div>}
            {!bogo && useCoupon && coupons.map((c) => (
              <div key={c.code} className="flex justify-between text-sm text-emerald-700"><span>Coupon {c.code}</span><span data-testid={`checkout-coupon-discount-${c.code}`}>−{formatMoney(c.amount)}</span></div>
            ))}
            {!bogo && !useCoupon && newsletterDiscount > 0 && coupons.length > 0 && (
              <p className="text-[11px] text-muted-foreground" data-testid="checkout-coupon-superseded">Your coupon isn't applied because your newsletter discount is a better deal (they can't be combined).</p>
            )}
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Shipping</span><span data-testid="checkout-shipping">{selectedMethod ? (shippingCost === 0 ? "Free" : formatMoney(shippingCost)) : "—"}</span></div>
            {protectionCost > 0 && <div className="flex justify-between text-sm"><span className="text-muted-foreground">Shipping protection</span><span data-testid="checkout-protection">{formatMoney(protectionCost)}</span></div>}
            <div className="flex justify-between items-center pt-3 mt-1 border-t border-border"><span className="text-sm text-muted-foreground">Total</span><span className="font-display font-bold text-2xl gold-text" data-testid="checkout-total">{formatMoney(total)}</span></div>
          </div>
          <p className="text-xs leading-relaxed text-muted-foreground mt-4" data-testid="checkout-total-note">Research-use-only materials. Your order is reviewed before dispatch.</p>
          <p className="text-xs font-semibold leading-relaxed text-foreground mt-2" data-testid="checkout-final-sale-note">All sales are final — no returns, exchanges, or refunds. By completing this order you agree to our all-sales-final policy.</p>
        </aside>
      </div>
    </div>
  );
}
